import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';

import { DocumentCreateModel, DocumentModel } from '@data-layer/models/documents';
import { DeliveryOrderModel, EntrySummaryModel, InvoiceModel, LineModel } from '@data-layer/models/entry-summaries';
import { ShipmentModel, ValidationMessageModel } from '@data-layer/models/common';
import {
  enumEntrySummaryDutiesPaymentType,
  enumEntrySummarySubmissionType,
  enumTransportationMode,
} from '@data-layer/enums/entry-summaries';
import {
  enumDocumentRelatedEntityType,
  enumDocumentRootEntityType,
  enumDocumentState,
  enumDocumentType,
} from '@data-layer/enums/documents';
import { enumBondRequestState } from '@data-layer/enums/common';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { flatten } from 'lodash-es';
import { DateHelper } from '@helpers/date.helper';
import { UtilsHelper } from '@helpers/utils-helper';
import { IEsDocumentStateUpdate } from '../../interfaces';
import { DocumentsService } from '@services/documents.service';
import { CompaniesService } from '@services/companies.service';
import { UserBetaFlagModel } from '@data-layer/models/user';
import { enumUserBetaFlag } from '@data-layer/enums/user';

type ExtendedDocument = DocumentModel & {
  params: { referenceNumber: string };
};

@Component({
  selector: 'es-attach-document',
  templateUrl: './es-attach-document.component.html',
  styleUrls: ['./es-attach-document.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EsAttachDocumentComponent implements OnChanges, OnDestroy {
  @Input() public deliveryOrdersInfo: DeliveryOrderModel[] = [];
  @Input() public entrySummary: EntrySummaryModel;
  @Input() public attachedDocuments: DocumentModel[] = [];
  @Input() public onlyLastVersionForFormDocuments: boolean = true;
  @Input() public isCanUploadDocuments: boolean = false;
  @Input() public isCanRemoveDocuments: boolean = false;
  @Input() public isOverrideDefaultPoaRequired: boolean = false;
  @Input() public canUploadPunToCompany: boolean = true;
  @Input() public isShowEmptyLineItemDocuments: boolean = true;
  @Input() public isHideDownloadAll: boolean;
  @Input() public validationMessages: ValidationMessageModel[] = [];

  @Input() public uploadCompanyFile: (document: DocumentCreateModel, file: File, uploadToCompany: boolean) => Observable<any>;
  @Input() public uploadFile: (document: DocumentCreateModel, file: File) => Observable<any>;
  @Input() public removeFile: (documentId: number) => Observable<any>;
  @Output() public stateUpdate: EventEmitter<{ documentId: number; state: enumDocumentState }> = new EventEmitter<{
    documentId: number;
    state: enumDocumentState;
  }>();
  @Output() public poaSidebarRequest: EventEmitter<enumDocumentType> = new EventEmitter<enumDocumentType>();

  public poaAttachedDocument: DocumentModel;
  public punAttachedDocument: DocumentModel;
  public deliveryOrderAttachedDocuments: ExtendedDocument[] = [];
  public notAttachedDocuments: DocumentModel[] = [];
  public otherAttachedDocuments: DocumentModel[] = [];
  public enumDocumentType: typeof enumDocumentType = enumDocumentType;
  public billName: string;
  public enumUserBetaFlag: typeof enumUserBetaFlag = enumUserBetaFlag;

  public viewAttachedDocuments: DocumentModel[] = [];

  public openPowerOfAttorneySidebar = () => this.onPoaSidebarRequest(enumDocumentType.POWER_OF_ATTORNEY);

  private lastDocumentForType: Map<enumDocumentType, DocumentModel> = new Map();

  private readonly docTypesForCheckingLastVersion: enumDocumentType[] = [
    enumDocumentType.CARGO_RELEASE_FORM_3461,
    enumDocumentType.CBP_FORM_7501,
    enumDocumentType.DELIVERY_ORDER,
  ];

  constructor(private documentsService: DocumentsService, private companiesService: CompaniesService) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.entrySummary || changes.attachedDocuments || changes.deliveryOrdersInfo || changes.onlyLastVersionForFormDocuments) {
      this.viewAttachedDocuments = this.onlyLastVersionForFormDocuments ? this.getOnlyLastVersion() : this.attachedDocuments;
      this.findDeliveryOrderDocuments();
      this.findNotAttachedDocuments();
      this.findOtherAttachedDocuments();
      if (this.isShowPoaDocument) {
        this.findDocument(enumDocumentType.POWER_OF_ATTORNEY);
      }
      if (this.isShowPunDocument) {
        this.findDocument(enumDocumentType.ACH_APPROVAL);
      }

      this.billName =
        this.entrySummary.transportationMode === enumTransportationMode.AIR ? 'FORM_LABEL.AIRWAY_BILL' : 'ENTRY_SUMMARY.BILL_OF_LADING';
    }
  }

  public get isShowPoaDocument(): boolean {
    return (
      this.entrySummary.submissionType === enumEntrySummarySubmissionType.BROKER_ASSISTED ||
      (this.entrySummary.submissionType === enumEntrySummarySubmissionType.MANUAL &&
        this.entrySummary.bondRequest &&
        this.entrySummary.bondRequest.state === enumBondRequestState.CREATED)
    );
  }

  public get isShowPunDocument(): boolean {
    return (
      (this.entrySummary.dutiesPaymentType === enumEntrySummaryDutiesPaymentType.ACH &&
        this.entrySummary.submissionType === enumEntrySummarySubmissionType.BROKER_ASSISTED) ||
      (this.entrySummary.submissionType === enumEntrySummarySubmissionType.MANUAL &&
        this.entrySummary.bondRequest &&
        this.entrySummary.bondRequest.state === enumBondRequestState.CREATED)
    );
    return true;
  }

  public onStateUpdate(data: IEsDocumentStateUpdate) {
    this.stateUpdate.emit(data);
  }

  public onPoaSidebarRequest(docType: enumDocumentType): void {
    this.poaSidebarRequest.emit(docType);
  }

  public ngOnDestroy(): void {
    // no-op
  }

  public onDownloadAllFiles(): void {
    const docIds: number[] = this.attachedFilteredDocuments.map((item: DocumentModel) => item.id);

    this.documentsService
      .getArchive(this.entrySummary.referenceNumber, docIds)
      .pipe(take(1))
      .subscribe((file: Blob) => {
        this.documentsService.downloadBlobFile(file, this.documentsService.formatZipFileName(this.entrySummary.referenceNumber));
      });
  }

  public get attachedFilteredDocuments(): DocumentModel[] {
    return this.attachedDocuments.filter((item: DocumentModel) => !!item.id);
  }

  public get isUploadedAnyFiles(): boolean {
    return !!this.attachedFilteredDocuments.length;
  }

  public get hasPoaDocusignFlag(): boolean {
    return this.companiesService
      .getUser()
      ?.betaFlags?.map((flag: UserBetaFlagModel) => flag.tag)
      .includes(enumUserBetaFlag.poaDocusign);
  }

  public get isTruck(): boolean {
    return this.entrySummary?.transportationMode === enumTransportationMode.TRUCK;
  }

  private findDocument(docType: enumDocumentType) {
    const newPOAorPUN: DocumentModel =
      this.viewAttachedDocuments.find((document) => document.relatedEntityId === this.entrySummary.id && document.type === docType) ||
      ({
        type: docType,
        relatedEntityId: this.entrySummary.id,
        relatedEntityType: enumDocumentRelatedEntityType.ENTRY_SUMMARY,
        rootEntityId: this.entrySummary.id,
        rootEntityType: enumDocumentRootEntityType.ENTRY_SUMMARY,
        fileName: null,
        url: null,
        createdDate: null,
        id: null,
      } as DocumentModel);
    switch (docType) {
      case enumDocumentType.POWER_OF_ATTORNEY:
        this.poaAttachedDocument = this.mergeDocument(this.poaAttachedDocument, newPOAorPUN);
        break;
      case enumDocumentType.ACH_APPROVAL:
        this.punAttachedDocument = this.mergeDocument(this.punAttachedDocument, newPOAorPUN);
        break;
    }
  }

  private findDeliveryOrderDocuments() {
    this.deliveryOrderAttachedDocuments = [];
    this.viewAttachedDocuments.forEach((document) => {
      if (document.type !== enumDocumentType.DELIVERY_ORDER) {
        return;
      }
      const relatedEntity: DeliveryOrderModel = this.findRelatedEntity(document) as any;
      if (relatedEntity) {
        this.deliveryOrderAttachedDocuments.push({ ...document, params: relatedEntity });
      }
    });
  }

  private findNotAttachedDocuments() {
    this.notAttachedDocuments = [];
    this.viewAttachedDocuments
      .filter((doc: DocumentModel) => doc.type !== enumDocumentType.FILE_PREVIEW)
      .forEach((document) => {
        const relatedEntity = this.findRelatedEntity(document);
        if (!relatedEntity) {
          this.notAttachedDocuments.push(document);
          return;
        }
      });
  }

  private findOtherAttachedDocuments() {
    this.otherAttachedDocuments = [];
    this.viewAttachedDocuments
      .filter((doc) => doc.type !== enumDocumentType.FILE_PREVIEW)
      .forEach((document) => {
        const relatedEntity = this.findRelatedEntity(document);
        const isPOAOrPUNForBroker =
          this.isShowPoaDocument &&
          [enumDocumentType.POWER_OF_ATTORNEY, enumDocumentType.POWER_OF_ATTORNEY_TRUCKER, enumDocumentType.ACH_APPROVAL].includes(
            document.type,
          );
        if (relatedEntity && relatedEntity.id === this.entrySummary.id && !isPOAOrPUNForBroker) {
          this.otherAttachedDocuments.push(document);
        }
      });
  }

  private getOnlyLastVersion() {
    return this.attachedDocuments.filter((doc) =>
      this.docTypesForCheckingLastVersion.indexOf(doc.type) !== -1 ? this.isLastVersion(doc) : true,
    );
  }

  private isLastVersion(doc: DocumentModel): boolean {
    return this.getLastVersion(doc.type, doc.relatedEntityId).id === doc.id;
  }

  private getLastVersion(type: enumDocumentType, relatedEntityId: number): DocumentModel {
    if (this.lastDocumentForType.has(type)) {
      return this.lastDocumentForType.get(type);
    }
    const result: DocumentModel = UtilsHelper.maxElementBy<DocumentModel, string>(
      this.attachedDocuments.filter((doc: DocumentModel) => doc.type === type && doc.relatedEntityId === relatedEntityId),
      'uploadedDate',
      DateHelper.getTime,
    );
    this.lastDocumentForType.set(type, result);
    return result;
  }

  private findRelatedEntity(document: DocumentModel): ShipmentModel | InvoiceModel | LineModel | EntrySummaryModel | DeliveryOrderModel {
    if (document.relatedEntityType === enumDocumentRelatedEntityType.ENTRY_SUMMARY) {
      return this.entrySummary;
    }

    if (document.relatedEntityType === enumDocumentRelatedEntityType.DELIVERY_ORDER) {
      return this.deliveryOrdersInfo.find((del) => del.id === document.relatedEntityId);
    }

    if (document.relatedEntityType === enumDocumentRelatedEntityType.SHIPMENT) {
      return this.findRelatedShipment(document);
    }

    if (document.relatedEntityType === enumDocumentRelatedEntityType.INVOICE) {
      return this.findRelatedInvoice(document);
    }

    if (document.relatedEntityType === enumDocumentRelatedEntityType.LINE_ITEM) {
      return this.findRelatedLineItem(document);
    }

    return null;
  }

  private findRelatedShipment(document: DocumentModel): ShipmentModel {
    return this.findRelatedObject(document, this.getAllShipment());
  }

  private findRelatedInvoice(document: DocumentModel): InvoiceModel {
    return this.findRelatedObject(document, this.getAllInvoices());
  }

  private findRelatedLineItem(document: DocumentModel): LineModel {
    return this.findRelatedObject(document, this.getAllLineItems());
  }

  private getAllShipment(): ShipmentModel[] {
    return this.entrySummary.shipments || [];
  }

  private getAllInvoices(): InvoiceModel[] {
    return flatten(this.getAllShipment().map((shipment: ShipmentModel) => shipment.invoices || []));
  }

  private getAllLineItems(): LineModel[] {
    return flatten(this.getAllInvoices().map((invoice: InvoiceModel) => invoice.lines || []));
  }

  private findRelatedObject<T>(document: DocumentModel, entities: T[]): T {
    return entities.find((entity) => document.relatedEntityId === entity['id']);
  }

  private mergeDocument(existedDocument: DocumentModel, newDocument: DocumentModel): DocumentModel {
    if (!existedDocument) {
      return newDocument;
    }
    Object.assign(existedDocument, newDocument);
    return existedDocument;
  }
}
